import { get } from 'utils/http';
import { BASE_URL } from 'constants/env';
import { IMAGE, VIDEO } from 'constants/media-types.constants';

export type MediaType = typeof IMAGE | typeof VIDEO;

export interface TripMediaResult {
    trip: TripSchema;
    media: MediaSchema[];
}

export type MediaSchema = {
    id: number;
    uuid: string | null;
    type: number;
    order: number | null;
    path: string | null;
    large_thumbnail_path: string;
    small_thumbnail_path: string | null;
    description: string | null;
    full_res_unavailable: boolean;
    full_res_width: number | null;
    full_res_height: number | null;
    duration: number | null;
    lat: number | null;
    lon: number | null;
    geolocation_from_step: boolean | null;
    step_id: number | null;
    readonly cdn_path: string | undefined;
    step?: {
        trip_id: number;
        id: number;
        start_time: string;
    };
};

export type StepSchema = {
    id: number;
    media?: Array<MediaSchema>;
    start_time: number;
};

export type TripSchema = {
    id: number;
    all_steps: Array<StepSchema>;
    name: string;
};

/**
 * Gets all media for a specific trip
 *
 * @param tripId The ID of the trip
 * @param allowedMediaTypes List of media types we want to get
 * @returns Promise with all media from the trip
 */
export const getTripMedia = (tripId: string, allowedMediaType: MediaType): Promise<TripMediaResult> => {
    return get<TripSchema>(`${BASE_URL}/api/trips/${tripId}`).then((trip) => {
        // Extract all media from all steps
        const allMedia = trip.all_steps.flatMap(
            (step) =>
                step.media?.map((media) => ({
                    ...media,
                    step: {
                        trip_id: trip.id,
                        id: step.id,
                        start_time: step.start_time.toString(),
                    },
                })) || []
        );

        // Filter by allowed media types
        const filteredMedia = allMedia.filter((media) => media.type === allowedMediaType);

        return {
            trip,
            media: filteredMedia,
        };
    });
};
